import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import axios from "axios";
import { ScanLine, MapPin, Calendar, Download, MessageCircle, AlertTriangle } from "lucide-react";
import {
  downloadTicketPdf,
  whatsappShareUrl,
} from "@/lib/ticketDistribution";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export default function PublicTicket() {
  const { code } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    axios
      .get(`${API}/tickets/public/${code}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e.response?.data?.detail || "Ticket not found"));
  }, [code]);

  if (err) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-[#F4F4F5]" data-testid="public-ticket-error">
        <div className="brut-card p-8 max-w-md w-full text-center">
          <AlertTriangle className="w-12 h-12 mx-auto mb-3 text-[#FF3B30]" />
          <h1 className="font-display text-2xl font-black uppercase tracking-tighter mb-2">
            Ticket Not Found
          </h1>
          <p className="text-sm text-[#666] font-mono break-all">{code}</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F4F4F5]">
        <div className="brut-spinner" />
      </div>
    );
  }

  const { event, ticket } = data;
  const publicUrl = window.location.href;
  const waUrl = whatsappShareUrl({ event, ticket, publicUrl });
  const isAttended = ticket.status === "attended";

  return (
    <div className="min-h-screen bg-[#F4F4F5] py-6 px-4" data-testid="public-ticket-page">
      <div className="max-w-md mx-auto">
        {/* Header */}
        <div className="flex items-center gap-2 mb-4">
          <div className="w-8 h-8 bg-[#FFCC00] border-2 border-[#121212] flex items-center justify-center">
            <ScanLine className="w-5 h-5" strokeWidth={2.5} />
          </div>
          <span className="font-display font-black tracking-tighter uppercase">
            Gatekeep
          </span>
        </div>

        {/* Ticket card */}
        <div className="brut-card overflow-hidden" data-testid="public-ticket-card">
          {/* Black header */}
          <div className="bg-[#121212] text-white p-4 flex items-center justify-between border-b-2 border-[#121212]">
            <div>
              <div className="mono-label text-[#FFCC00]">// TICKET</div>
              <div className="font-display text-3xl font-black tracking-tighter">
                #{ticket.serial}
              </div>
            </div>
            {isAttended ? (
              <div className="px-2 py-1 bg-[#FF3B30] text-white border-2 border-white mono-label" data-testid="ticket-attended-badge">
                Used
              </div>
            ) : (
              <div className="px-2 py-1 bg-[#00C853] text-white border-2 border-white mono-label" data-testid="ticket-valid-badge">
                Valid
              </div>
            )}
          </div>

          {/* QR */}
          <div className="bg-white p-6 flex items-center justify-center border-b-2 border-[#121212]">
            <div className="bg-white p-3 border-2 border-[#121212]">
              <QRCodeSVG value={ticket.code} size={220} level="M" />
            </div>
          </div>

          {/* Event details */}
          <div className="p-5 space-y-3">
            <div>
              <div className="mono-label text-[#666]">// EVENT</div>
              <h1 className="font-display text-2xl font-black uppercase tracking-tight leading-tight break-words" data-testid="public-event-name">
                {event.name}
              </h1>
            </div>
            {event.venue && (
              <div className="flex items-start gap-2 text-sm">
                <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>{event.venue}</span>
              </div>
            )}
            <div className="flex items-start gap-2 text-sm">
              <Calendar className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>
                {fmtDate(event.start_at)}
                {event.end_at ? ` → ${fmtDate(event.end_at)}` : ""}
              </span>
            </div>
            <div className="font-mono text-xs text-[#666] break-all border-t-2 border-[#121212] pt-3">
              CODE: {ticket.code}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="grid grid-cols-2 gap-3 mt-4">
          <button
            onClick={() => downloadTicketPdf({ event, ticket, publicUrl })}
            className="brut-btn inline-flex items-center justify-center gap-2"
            data-testid="public-download-pdf-btn"
          >
            <Download className="w-4 h-4" /> PDF
          </button>
          <a
            href={waUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="brut-btn-accent inline-flex items-center justify-center gap-2 no-underline"
            data-testid="public-whatsapp-btn"
          >
            <MessageCircle className="w-4 h-4" /> WhatsApp
          </a>
        </div>

        <p className="mt-6 text-center text-xs text-[#666] font-mono">
          Show this QR at the entrance for admission.
        </p>
      </div>
    </div>
  );
}
